import { useState, useEffect, useRef } from 'react';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCompany } from '@/contexts/CompanyContext';
import { cn } from '@/lib/utils';

export function CompanySelector() {
  const { currentCompany, availableCompanies, switchCompany, isLoading, refreshCompanyList } = useCompany();
  const [open, setOpen] = useState(false);
  const refreshCompanyListRef = useRef(refreshCompanyList);
  refreshCompanyListRef.current = refreshCompanyList;

  // Refresh company list after the popover has fully opened
  useEffect(() => {
    if (open) {
      refreshCompanyListRef.current();
    }
  }, [open]);

  const handleSelect = async (companyId: string) => {
    if (companyId === String(currentCompany?.id)) {
      setOpen(false);
      return;
    }

    try {
      await switchCompany(companyId);
      setOpen(false);
    } catch (error) {
      console.error('Failed to switch company:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-r from-primary/15 to-primary/10 border-2 border-primary/30 shadow-sm">
        <Building2 className="h-3.5 w-3.5 text-primary" />
        <span className="font-bold text-primary text-xs">Loading...</span>
      </div>
    );
  }

  if (!currentCompany || availableCompanies.length === 0) {
    return null;
  }

  // If only one company, show it without dropdown
  if (availableCompanies.length === 1) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-r from-primary/15 to-primary/10 border-2 border-primary/30 shadow-sm">
        <Building2 className="h-3.5 w-3.5 text-primary" />
        <span className="font-bold text-primary text-xs">{currentCompany.name}</span>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="h-auto py-1 px-2.5 rounded-md bg-gradient-to-r from-primary/15 to-primary/10 border-2 border-primary/30 shadow-sm hover:shadow-md transition-all duration-200 hover:bg-primary/20"
        >
          <div className="flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-primary" />
            <span className="font-bold text-primary text-xs">{currentCompany.name}</span>
            <ChevronsUpDown className="h-3 w-3 text-primary/70" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search company..." className="h-9" />
          <CommandEmpty>No company found.</CommandEmpty>
          <CommandGroup>
            {availableCompanies.map((company) => (
              <CommandItem
                key={company.id}
                value={String(company.id)}
                onSelect={() => handleSelect(String(company.id))}
                className="cursor-pointer"
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    currentCompany.id === company.id ? 'opacity-100 text-primary' : 'opacity-0'
                  )}
                />
                <div className="flex items-center gap-2 flex-1">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm">{company.name}</span>
                    <span className="text-xs text-muted-foreground">
                      Code: {company.code}{company.instanceId && ` | Instance: ${company.instanceId}`}
                    </span>
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
